import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { ImageUploader } from "../components/ImageUploader";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const SLIDES = [
  { src: "/login-dream.jpg", to: "/buy", label: "Find your dream home", pos: "left center" },
  { src: "/login-rent.jpg", to: "/rent", label: "View premium rentals", pos: "left center" },
  { src: "/login-sell.jpg", to: "/sell", label: "List with M&D — sell for top value", pos: "left center" },
  { src: "/login-water.jpg", to: "/buy", label: "Explore South Florida waterfront luxury", pos: "center" },
];

function Shell({ children }) {
  const { t } = useLang();
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 5500);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block overflow-hidden bg-[#0e0b07]" data-testid="auth-slideshow">
        {SLIDES.map((s, i) => (
          <Link
            key={i} to={s.to} aria-label={s.label} data-testid={`auth-slide-${i}`}
            className="absolute inset-0 transition-opacity duration-[1200ms] ease-in-out"
            style={{ opacity: i === idx ? 1 : 0, pointerEvents: i === idx ? "auto" : "none" }}
          >
            <img src={s.src} alt={s.label} className="w-full h-full object-cover" style={{ objectPosition: s.pos }} />
          </Link>
        ))}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex gap-2.5" data-testid="auth-slide-dots">
          {SLIDES.map((s, i) => (
            <button
              key={i} onClick={() => setIdx(i)} aria-label={`Go to slide ${i + 1}`} data-testid={`auth-dot-${i}`}
              className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-7 bg-[#f0cf88]" : "w-2 bg-white/55 hover:bg-white/90"}`}
            />
          ))}
        </div>
      </div>
      <div className="flex items-center justify-center px-5 py-16 relative">
        <Link to="/" className="absolute top-6 left-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-primary" data-testid="auth-home"><ArrowLeft size={16} /> {t("brand")}</Link>
        <div className="w-full max-w-md">{children}</div>
      </div>
    </div>
  );
}

export function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const res = await login(form.email, form.password);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <Shell>
      <h1 className="font-serif text-4xl font-light">{t("auth.welcome")}</h1>
      <p className="text-muted-foreground font-light mt-2">{t("auth.welcomeSub")}</p>
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
        {error && <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 px-3 py-2" data-testid="login-error">{error}</p>}
        <div>
          <label className="label-caps text-muted-foreground">{t("auth.email")}</label>
          <input type="text" autoComplete="username" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email"
            className="w-full mt-2 bg-background border border-border focus:border-primary px-3 py-3 text-sm outline-none transition-colors" />
        </div>
        <div>
          <label className="label-caps text-muted-foreground">{t("auth.password")}</label>
          <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password"
            className="w-full mt-2 bg-background border border-border focus:border-primary px-3 py-3 text-sm outline-none transition-colors" />
        </div>
        <button disabled={loading} className="w-full py-3.5 bg-primary text-primary-foreground label-caps disabled:opacity-50 flex items-center justify-center gap-2 hover:brightness-110 transition-[filter]" data-testid="login-submit">
          {loading ? <><Loader2 size={16} className="animate-spin" /> {t("auth.signingIn")}</> : t("auth.signin")}
        </button>
      </form>
      <p className="mt-6 text-sm text-muted-foreground text-center">{t("auth.noAccount")} <Link to="/register" className="text-primary hover:underline" data-testid="to-register">{t("auth.signup")}</Link></p>
      <div className="mt-6 p-4 border border-border bg-card/40 text-xs text-muted-foreground">
        <p className="label-caps text-primary mb-2">Demo Accounts</p>
        <p>Admin: admin@estatelux.com / Admin@123</p>
        <p>Agent: sofia@estatelux.com / Demo@123</p>
        <p>Buyer: buyer@estatelux.com / Demo@123</p>
      </div>
    </Shell>
  );
}

export function Register() {
  const { t, lang } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "client", photo: "", title: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isAgent = form.role === "agent";
  const missingPhoto = isAgent && !form.photo;

  const submit = async (e) => {
    e.preventDefault();
    if (missingPhoto) { setError(lang === "es" ? "Sube una foto de perfil para tu cuenta de agente." : "Please upload a profile photo for your agent account."); return; }
    setError(""); setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <Shell>
      <h1 className="font-serif text-4xl font-light">{t("auth.joinTitle")}</h1>
      <p className="text-muted-foreground font-light mt-2">{t("auth.joinSub")}</p>
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
        {error && <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 px-3 py-2" data-testid="register-error">{error}</p>}
        <div>
          <label className="label-caps text-muted-foreground">{t("auth.name")}</label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name"
            className="w-full mt-2 bg-background border border-border focus:border-primary px-3 py-3 text-sm outline-none transition-colors" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label-caps text-muted-foreground">{t("auth.email")}</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email"
              className="w-full mt-2 bg-background border border-border focus:border-primary px-3 py-3 text-sm outline-none transition-colors" />
          </div>
          <div>
            <label className="label-caps text-muted-foreground">{t("auth.phone")}</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="register-phone"
              className="w-full mt-2 bg-background border border-border focus:border-primary px-3 py-3 text-sm outline-none transition-colors" />
          </div>
        </div>
        <div>
          <label className="label-caps text-muted-foreground">{t("auth.password")}</label>
          <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password"
            className="w-full mt-2 bg-background border border-border focus:border-primary px-3 py-3 text-sm outline-none transition-colors" />
        </div>
        <div>
          <label className="label-caps text-muted-foreground">{t("auth.role")}</label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger className="mt-2 rounded-none py-3 h-auto" data-testid="register-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="client">{t("auth.roleClient")}</SelectItem>
              <SelectItem value="agent">{t("auth.roleAgent")}</SelectItem>
              <SelectItem value="owner">{t("auth.roleOwner")}</SelectItem>
              <SelectItem value="referral">{t("auth.roleReferral")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {isAgent && (
          <div className="space-y-4 border border-primary/20 bg-primary/5 p-4 rounded-md" data-testid="register-agent-fields">
            <div>
              <label className="label-caps text-muted-foreground">{lang === "es" ? "Foto de perfil" : "Profile photo"} <span className="text-accent">*</span></label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">{lang === "es" ? "Aparecerá en tu perfil público de agente." : "This appears on your public agent profile."}</p>
              <ImageUploader single aspect={1} value={form.photo ? [form.photo] : []} onChange={(a) => setForm({ ...form, photo: a[0] || "" })} lang={lang} />
            </div>
            <div>
              <label className="label-caps text-muted-foreground">{lang === "es" ? "Título / cargo" : "Title / role"}</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="register-title" placeholder={lang === "es" ? "Ej: Asesor de Lujo" : "e.g. Luxury Advisor"}
                className="w-full mt-2 bg-background border border-border focus:border-primary px-3 py-3 text-sm outline-none transition-colors" />
            </div>
          </div>
        )}
        <button disabled={loading} className="w-full py-3.5 bg-primary text-primary-foreground label-caps disabled:opacity-50 flex items-center justify-center gap-2 hover:brightness-110 transition-[filter]" data-testid="register-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : t("auth.signup")}
        </button>
      </form>
      <p className="mt-6 text-sm text-muted-foreground text-center">{t("auth.haveAccount")} <Link to="/login" className="text-primary hover:underline" data-testid="to-login">{t("auth.signin")}</Link></p>
    </Shell>
  );
}
